import React, { useMemo } from 'react';
import { EditableTable, EditableTableColumn, EditableTableGroup } from 'src/presentations/components/organisms/editableTable/EditableTable';
import 'src/presentations/components/organisms/editableTable/EditableTable.css';
import { InvoiceBankDetailMessageDto, InvoiceMessageDto } from 'src/generated/services/TFinancialApi';
import { ValidateNode } from 'src/use-cases/internalDashboard/useValidation';
import { ValidateInvoiceStep } from 'src/presentations/components/molecules/uploadInvoice/types';

interface BankDetailsTableProps {
  bankDetails: InvoiceBankDetailMessageDto[];
  onUpdate: (bankDetails: InvoiceBankDetailMessageDto[]) => void;
  validateJson?: ValidateNode<any>;
  validateUpdatedJson?: ValidateNode<any>;
  activeStep: ValidateInvoiceStep;
  ocrMessage: InvoiceMessageDto;
}

export const BankDetailsTable = ({ bankDetails, onUpdate, validateJson, validateUpdatedJson, activeStep, ocrMessage }: BankDetailsTableProps) => {
  const columns: EditableTableColumn[] = useMemo(
    () => [
      { key: 'bankName', label: 'Bank Name', width: 200 },
      { key: 'bankBranch', label: 'Bank Branch', width: 300 },
      { key: 'swiftCode', label: 'Swift Code', width: 140 },
      { key: 'accountName', label: 'Account Name', width: 180 },
      { key: 'accountNumber', label: 'Account Number', width: 180 },
    ],
    []
  );

  // Helper to get validation for a bank detail row/field
  const getBankDetailValidation = (rowIdx: number, field: string) => {
    const bankValidation = (validateJson?.bankDetails as any)?.value?.[rowIdx]?.value;
    const updatedBankValidation = (validateUpdatedJson?.bankDetails as any)?.value?.[rowIdx]?.value;
    const fieldValidation = bankValidation?.[field];
    const updatedFieldValidation = updatedBankValidation?.[field];
    let originalValue;
    if (ocrMessage?.bankDetails) {
      originalValue = ocrMessage.bankDetails?.[rowIdx]?.[field];
    }
    return {
      error: !!fieldValidation && fieldValidation.isValid === false,
      helperText: fieldValidation?.exception?.error || '',
      updatedError: !!updatedFieldValidation && updatedFieldValidation.isValid === false,
      originalValue,
    };
  };

  const group: EditableTableGroup = {
    label: '',
    rows: bankDetails.map((row, idx) => {
      const rowWithValidation = { ...row };
      columns.forEach((col) => {
        const v = getBankDetailValidation(idx, col.key);
        rowWithValidation[`${col.key}__error`] = v.error;
        rowWithValidation[`${col.key}__helperText`] = v.helperText;
        rowWithValidation[`${col.key}__updatedError`] = v.updatedError;
        rowWithValidation[`${col.key}__originalValue`] = v.originalValue;
      });
      return rowWithValidation;
    }),
    expanded: true,
    onToggle: () => {},
    onAdd: () => {
      onUpdate([
        ...bankDetails,
        {
          bankName: '',
          bankBranch: '',
          swiftCode: '',
          accountName: '',
          accountNumber: '',
        } as InvoiceBankDetailMessageDto,
      ]);
    },
  };

  const handleDelete = (_groupKey: string, idx: number) => {
    const newBankDetails = [...bankDetails];
    newBankDetails.splice(idx, 1);
    onUpdate(newBankDetails);
  };

  const handleCellEdit = (_groupKey: string, idx: number, field: string, value: any) => {
    const newBankDetails = [...bankDetails];
    newBankDetails[idx] = { ...newBankDetails[idx], [field]: value };
    onUpdate(newBankDetails);
  };

  return (
    <div className='h-[400px] flex flex-col'>
      <div className='flex-1 overflow-auto'>
        <EditableTable columns={columns} groups={[group]} onDelete={handleDelete} onCellEdit={handleCellEdit} activeStep={activeStep} />
      </div>
    </div>
  );
};
